import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { Chart, registerables } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
Chart.register(...registerables, ChartDataLabels);

// Funções utilitárias
const formatarData = (data) => {
    if (!data) return "N/A";
    const date = new Date(data);
    if (isNaN(date.getTime())) return "N/A";
    const dia = String(date.getUTCDate()).padStart(2, '0');
    const mes = String(date.getUTCMonth() + 1).padStart(2, '0');
    const ano = date.getUTCFullYear();
    return `${dia}/${mes}/${ano}`;
};

const formatarNumero = (num) => {
    if (num === null || num === undefined || String(num).trim() === '') return "N/A";
    const numero = Number(String(num).replace(',', '.'));
    if (isNaN(numero)) return "N/A";
    return numero.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const truncateText = (text, maxLength) => {
    const textAsString = String(text ?? '');
    if (textAsString.length <= maxLength) {
        return textAsString;
    }
    return textAsString.substring(0, maxLength - 3) + '...';
};

/**
 * Gera o relatório de imóveis com base nos filtros e atributos selecionados.
 */
export const gerarRelatorioDeImoveis = async ({ filteredImoveis, reportFilters, getFriendlyName, chartContainerRef, logo }) => {
    const colunasSelecionadas = Object.keys(reportFilters.atributos)
        .filter(key => reportFilters.atributos[key])
        .map(key => ({ key, name: getFriendlyName(key) }));

    if (colunasSelecionadas.length === 0) {
        alert("Por favor, selecione pelo menos um atributo para o relatório.");
        return;
    }

    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const THEME_COLOR = [56, 189, 114];
    const LIGHT_GRAY = [220, 220, 220];
    const TEXT_COLOR_DARK = '#333333';

    const pageHeader = (data) => {
        if (data.pageNumber === 1) {
            try {
                if (logo) doc.addImage(logo, 'PNG', 15, 12, 25, 8);
            } catch (e) { console.error("Erro ao adicionar logo:", e); }
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(22);
            doc.setTextColor(...THEME_COLOR);
            doc.text('Relatório de Imóveis', 15, 30);
            doc.setDrawColor(...THEME_COLOR);
            doc.setLineWidth(0.3);
            doc.line(15, 33, pageWidth - 15, 33);
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(9);
            doc.setTextColor(128);
            doc.text(`Gerado em: ${new Date().toLocaleString('pt-BR')}`, pageWidth - 15, 30, { align: 'right' });
        }
    };

    const pageFooter = (data) => {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.setTextColor(150);
        doc.text(`Página ${data.pageNumber}`, pageWidth / 2, pageHeight - 10, { align: 'center' });
    };

    const head = [colunasSelecionadas.map(col => col.name)];
    const body = filteredImoveis.map(imovel => (
        colunasSelecionadas.map(col => {
            const valor = imovel[col.key];
            if (['data_plantio', 'vencimento_contrato', 'data_contrato'].includes(col.key)) return formatarData(valor);
            if (['area_imovel', 'area_plantio', 'altura_desrama'].includes(col.key)) return formatarNumero(valor);
            if (['num_arvores_plantadas', 'num_arvores_cortadas', 'num_arvores_remanescentes'].includes(col.key)) {
                return (Number(valor) || 0).toLocaleString('pt-BR');
            }
            return truncateText(valor, 30);
        })
    ));

    let tableFontSize = 8.5;
    if (colunasSelecionadas.length > 12) tableFontSize = 7;
    doc.autoTable({
        head, body, startY: 40, showHead: 'firstPage',
        didDrawPage: (data) => { pageHeader(data); pageFooter(data); },
        margin: { top: 38, bottom: 20 }, theme: 'grid',
        styles: { fontSize: tableFontSize, cellPadding: 2.5, valign: 'middle', lineColor: LIGHT_GRAY, lineWidth: 0.1, textColor: TEXT_COLOR_DARK },
        headStyles: { fillColor: THEME_COLOR, textColor: 255, fontStyle: 'bold', halign: 'center', fontSize: tableFontSize + 0.5, lineColor: THEME_COLOR },
    });
    
    doc.addPage();
    const chartPageNumber = doc.internal.getNumberOfPages();
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(20);
    doc.setTextColor(...THEME_COLOR);
    doc.text('Dashboard de Análise', pageWidth / 2, 20, { align: 'center' });
    pageFooter({ pageNumber: chartPageNumber });

    const addChartToPdf = async (chartId, chartConfig, x, y, width, height) => {
        const container = chartContainerRef.current;
        if (!container) return;
        const canvas = document.createElement('canvas');
        canvas.id = chartId;
        canvas.width = width * 3;
        canvas.height = height * 3;
        container.appendChild(canvas);
        try {
            const backgroundColorPlugin = {
                id: 'customBackgroundColor',
                beforeDraw: (chart) => {
                    const { ctx } = chart;
                    ctx.save();
                    ctx.fillStyle = 'white';
                    ctx.fillRect(0, 0, chart.width, chart.height);
                    ctx.restore();
                }
            };
            chartConfig.plugins = [...(chartConfig.plugins || []), backgroundColorPlugin];
            const chart = new Chart(canvas.getContext('2d'), chartConfig);
            await new Promise(resolve => setTimeout(resolve, 500));
            const imgData = chart.toBase64Image('image/png', 1.0);
            doc.addImage(imgData, 'PNG', x, y, width, height, undefined, 'FAST');
            chart.destroy();
        } finally {
            container.removeChild(canvas);
        }
    };
    
    const areaPorEspecie = filteredImoveis.reduce((acc, imovel) => {
        const especie = imovel.especie || 'N/A';
        const area = parseFloat(String(imovel.area_plantio).replace(',', '.')) || 0;
        acc[especie] = (acc[especie] || 0) + area;
        return acc;
    }, {});

    const areaChartConfig = {
        type: 'pie',
        data: {
            labels: Object.keys(areaPorEspecie),
            datasets: [{ data: Object.values(areaPorEspecie), backgroundColor: ['#2ecc71', '#3498db', '#f1c40f', '#e74c3c', '#9b59b6'] }]
        },
        options: { plugins: { legend: { position: 'bottom' } } }
    };
    await addChartToPdf('areaChartCanvas', areaChartConfig, 20, 35, 120, 120);
    
    const imoveisPorMunicipio = filteredImoveis.reduce((acc, imovel) => {
        const municipio = imovel.municipio || 'N/A';
        acc[municipio] = (acc[municipio] || 0) + 1;
        return acc;
    }, {});

    const municipioChartConfig = {
        type: 'bar',
        data: {
            labels: Object.keys(imoveisPorMunicipio),
            datasets: [{ label: 'Nº de Imóveis', data: Object.values(imoveisPorMunicipio), backgroundColor: '#3498db' }]
        },
        options: { indexAxis: 'y' }
    };
    await addChartToPdf('municipioChartCanvas', municipioChartConfig, 150, 35, 120, 120);

    doc.save(`relatorio_imoveis_${new Date().toISOString().slice(0,10)}.pdf`);
};

/**
 * Gera o relatório de previsão de desrama ou desbaste.
 * O parâmetro 'token' foi adicionado.
 */
export const gerarRelatorioDePrevisao = async ({ tipo, anoPrevisao, imoveis, logo, apiBaseUrl, token }) => {
    const endpoint = tipo === 'desrama' ? `/api/desramas/previsoes/ano/${anoPrevisao}` : `/api/desbastes/previsoes/ano/${anoPrevisao}`;
    const tituloRelatorio = tipo === 'desrama' ? `Previsão de Desrama - ${anoPrevisao}` : `Previsão de Desbaste - ${anoPrevisao}`;
    const nomeArquivo = `previsao_${tipo}_${anoPrevisao}.pdf`;

    if (!token) {
        alert("Erro de autenticação. Por favor, faça login novamente.");
        return;
    }

    try {
        const response = await fetch(`${apiBaseUrl}${endpoint}`, {
            headers: { 
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}` // *** CORREÇÃO APLICADA AQUI ***
            }
        });
        const previsoes = await response.json();

        if (!response.ok) {
            if (response.status === 401 || response.status === 400) {
                 throw new Error("Sessão expirada ou inválida. Faça login novamente.");
            }
            throw new Error(previsoes.error || 'Acesso negado.');
        }

        if (previsoes.length === 0) {
            alert(`Nenhuma previsão de ${tipo} encontrada para ${anoPrevisao}.`);
            return;
        }

        const dadosCompletos = previsoes.map(previsao => {
            const imovelCompleto = imoveis.find(imovel => imovel.id === previsao.imovel_id);
            return {
                ...previsao,
                num_arvores_remanescentes: imovelCompleto?.num_arvores_remanescentes || 0
            };
        });

        const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
        const pageWidth = doc.internal.pageSize.getWidth();
        const THEME_COLOR = [56, 189, 114];

        if (logo) doc.addImage(logo, 'PNG', 15, 12, 25, 8);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(18);
        doc.setTextColor(...THEME_COLOR);
        doc.text(tituloRelatorio, pageWidth / 2, 20, { align: 'center' });
        
        const head = [['Código CC', 'Descrição do Imóvel', 'Proprietário', 'Árv. Reman.', 'Data Previsão']];
        const body = dadosCompletos.map(item => [
            item.codigo_cc || 'N/A',
            truncateText(item.descricao, 35),
            truncateText(item.proprietario, 25),
            (item.num_arvores_remanescentes || 0).toLocaleString('pt-BR'),
            formatarData(item.previsao)
        ]);

        const totalArvores = dadosCompletos.reduce((sum, item) => sum + (Number(item.num_arvores_remanescentes) || 0), 0);

        doc.autoTable({
            head, 
            body, 
            startY: 40, 
            theme: 'grid',
            headStyles: { fillColor: THEME_COLOR, textColor: 255, halign: 'center' },
            footStyles: { fillColor: [240, 240, 240], textColor: 0, fontStyle: 'bold' },
            styles: { fontSize: 9, cellPadding: 2, valign: 'middle' },
            columnStyles: { 3: { halign: 'center' } },
            foot: [[
                { content: 'Total de Árvores:', colSpan: 3, styles: { halign: 'right', fontStyle: 'bold' } },
                { content: totalArvores.toLocaleString('pt-BR'), styles: { halign: 'center', fontStyle: 'bold' } },
                ''
            ]],
        });
        
        doc.save(nomeArquivo);

    } catch (error) {
        console.error(`Erro ao gerar relatório de ${tipo}:`, error);
        alert(`Erro ao gerar relatório: ${error.message}`);
    }
};

/**
 * Gera o relatório de imóveis com vencimento de contrato em um ano específico.
 */
export const gerarRelatorioDeVencimentos = async ({ anoVencimento, imoveis, logo }) => {
    if (!anoVencimento) {
        alert('Por favor, insira um ano para gerar o relatório de vencimentos.');
        return;
    }

    const imoveisFiltrados = imoveis.filter(imovel => {
        if (!imovel.vencimento_contrato) return false;
        const anoDoContrato = new Date(imovel.vencimento_contrato).getUTCFullYear();
        return anoDoContrato === parseInt(anoVencimento, 10);
    });

    if (imoveisFiltrados.length === 0) {
        alert(`Nenhum imóvel encontrado com vencimento de contrato no ano de ${anoVencimento}.`);
        return;
    }

    const totalAreaPlantio = imoveisFiltrados.reduce((sum, item) => {
        const area = Number(String(item.area_plantio || '0').replace(',', '.'));
        return sum + (isNaN(area) ? 0 : area);
    }, 0);

    const totalArvoresRemanescentes = imoveisFiltrados.reduce((sum, item) => {
        return sum + (Number(item.num_arvores_remanescentes) || 0);
    }, 0);

    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const THEME_COLOR = [56, 189, 114];
    const tituloRelatorio = `Relatório de Vencimentos - ${anoVencimento}`;
    const nomeArquivo = `relatorio_vencimentos_${anoVencimento}.pdf`;

    if (logo) doc.addImage(logo, 'PNG', 15, 12, 25, 8);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18);
    doc.setTextColor(...THEME_COLOR);
    doc.text(tituloRelatorio, pageWidth / 2, 20, { align: 'center' });
    
    const head = [['Código CC', 'Descrição do Imóvel', 'Data de Vencimento', 'Área Plantio (ha)', 'Árv. Remanescentes']];
    const body = imoveisFiltrados.map(item => [
        item.codigo_cc || 'N/A',
        truncateText(item.descricao, 40),
        formatarData(item.vencimento_contrato),
        formatarNumero(item.area_plantio),
        (item.num_arvores_remanescentes || 0).toLocaleString('pt-BR'),
    ]);

    doc.autoTable({
        head, 
        body, 
        startY: 40, 
        theme: 'grid',
        headStyles: { fillColor: THEME_COLOR, textColor: 255, halign: 'center' },
        footStyles: { fillColor: [245, 245, 245], textColor: 0, fontStyle: 'bold' },
        styles: { fontSize: 9, cellPadding: 2.5, valign: 'middle' },
        columnStyles: { 
            2: { halign: 'center' }, 
            3: { halign: 'center' }, 
            4: { halign: 'center' }
        },
        foot: [[
            { content: 'Totais:', colSpan: 2, styles: { halign: 'right', fontStyle: 'bold' } },
            '',
            { content: formatarNumero(totalAreaPlantio), styles: { halign: 'center', fontStyle: 'bold' } },
            { content: totalArvoresRemanescentes.toLocaleString('pt-BR'), styles: { halign: 'center', fontStyle: 'bold' } }
        ]],
    });
    
    doc.save(nomeArquivo);
};